import { DataTable, Switch, type TanstackReactTable } from "@devhop/ui";
import { EditIcon, ImageIcon, Trash2Icon } from "lucide-react";
import { useMemo } from "react";
import { formatPrice } from "@/shared/lib/currency";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import { RowActions } from "@/shared/ui/RowActions";
import type { ShopSellableCatalogRow } from "../api/client";
import type { ShopSkuMediaCount } from "../api/queries";

type ShopCatalogTableProps = {
  data: ShopSellableCatalogRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  canUpdate?: boolean;
  onEdit?: (row: ShopSellableCatalogRow) => void;
  /** ເປີດ/ປິດ SKU ຂາຍໄດ້ — ສະຫຼັບ isActive */
  onToggleActive?: (row: ShopSellableCatalogRow) => void;
  /** ລຶບ SKU ອອກຈາກຮ້ານ (ບລັອກຝັ່ງ server ຖ້າຖືກໃຊ້/ຂາຍແລ້ວ) */
  onDelete?: (row: ShopSellableCatalogRow) => void;
  /** จำนวนรูป/รูปแรกต่อ sellable — ยังโหลดไม่เสร็จก็ปล่อยว่างไว้ */
  mediaCounts?: Map<string, ShopSkuMediaCount>;
  onViewImages?: (row: ShopSellableCatalogRow) => void;
};

export function ShopCatalogTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  canUpdate = false,
  onEdit,
  onToggleActive,
  onDelete,
  mediaCounts,
  onViewImages,
}: ShopCatalogTableProps) {
  const columns: TanstackReactTable.ColumnDef<ShopSellableCatalogRow>[] =
    useMemo(
      () => [
        {
          id: "image",
          header: "ຮູບ",
          cell: ({ row }) => {
            const r = row.original;
            const counts = mediaCounts?.get(r.id);
            // imageUrl มาจาก list query แล้ว — ถ้ายังว่างค่อยใช้รูปแรกจาก media ที่ prefetch
            const url = resolveImageSrc(r.imageUrl ?? counts?.firstUrl);
            const label = `ເບິ່ງຮູບທັງໝົດຂອງ ${r.productName}`;
            return (
              <button
                type="button"
                onClick={() => onViewImages?.(r)}
                disabled={!onViewImages}
                aria-label={label}
                title={label}
                className="relative flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-muted transition-colors hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default disabled:hover:border-border"
              >
                {url ? (
                  <img
                    src={url}
                    alt={r.productName}
                    className="size-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <ImageIcon
                    className="size-5 text-muted-foreground"
                    aria-hidden
                  />
                )}
                <span className="absolute inset-x-0 bottom-0 bg-primary/90 text-center font-medium text-[10px] text-primary-foreground leading-4">
                  {counts ? `${counts.count} ຮູບ` : "…"}
                </span>
              </button>
            );
          },
          size: 64,
        },
        {
          id: "productName",
          header: "ຊື່ສິນຄ້າ",
          cell: ({ row }) => {
            const r = row.original;
            return (
              <div className="flex flex-col items-start gap-0.5">
                <span className="font-medium">{r.productName}</span>
                {r.status === "candidate" ? (
                  <span
                    className="w-fit rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 font-medium text-[11px] text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400"
                    title="ລໍ Admin ອະນຸມັດ — ຍັງຂາຍຢູ່ POS ບໍ່ໄດ້"
                  >
                    ລໍຖ້າອະນຸມັດ
                  </span>
                ) : null}
                {r.status === "archived" ? (
                  <span
                    className="w-fit rounded-full border border-red-300 bg-red-50 px-1.5 py-0.5 font-medium text-[11px] text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-400"
                    title="Admin ປະຕິເສດ master ນີ້ — ຂາຍທີ່ POS ບໍ່ໄດ້"
                  >
                    ຖືກ Admin ປະຕິເສດ
                  </span>
                ) : null}
                {r.status !== "archived" && !r.isActive ? (
                  <span
                    className="w-fit rounded-full border border-muted-foreground/30 bg-muted px-1.5 py-0.5 font-medium text-[11px] text-muted-foreground"
                    title="ຮ້ານປິດ SKU ນີ້ — ຂາຍທີ່ POS ບໍ່ໄດ້"
                  >
                    ປິດຢູ່
                  </span>
                ) : null}
              </div>
            );
          },
          size: 180,
        },
        {
          id: "shopSku",
          header: "SKU",
          cell: ({ row }) => (
            <span className="font-mono text-muted-foreground text-sm">
              {row.original.shopSku}
            </span>
          ),
          size: 100,
        },
        {
          id: "barcode",
          header: "Barcode",
          cell: ({ row }) => (
            <span className="font-mono text-muted-foreground text-sm">
              {row.original.barcode ?? "—"}
            </span>
          ),
          size: 100,
        },
        {
          id: "price",
          header: "ລາຄາ",
          cell: ({ row }) =>
            formatPrice(row.original.price, row.original.currency),
          size: 100,
        },
        {
          id: "stock",
          header: "ສະຕ໋ອກ",
          cell: ({ row }) => {
            const qoh = row.original.quantityOnHand;
            if (qoh === null) {
              return <span className="text-muted-foreground text-sm">—</span>;
            }
            return (
              <span className="font-medium text-sm tabular-nums">{qoh}</span>
            );
          },
          size: 72,
        },
        {
          id: "categoryName",
          header: "ໝວດ",
          cell: ({ row }) => (
            <span className="text-muted-foreground text-sm">
              {row.original.categoryName ?? "—"}
            </span>
          ),
          size: 100,
        },
        {
          id: "brandName",
          header: "ແບຣນ",
          cell: ({ row }) => (
            <span className="text-muted-foreground text-sm">
              {row.original.brandName ?? "—"}
            </span>
          ),
          size: 100,
        },
        {
          id: "oemPartNo",
          header: "OEM",
          cell: ({ row }) => (
            <span className="text-muted-foreground text-sm">
              {row.original.oemPartNo ?? "—"}
            </span>
          ),
          size: 90,
        },
        ...(canUpdate && onToggleActive
          ? [
              {
                id: "active",
                header: "ເປີດຂາຍ",
                size: 84,
                cell: ({ row }) => {
                  const r = row.original;
                  // Rejected (archived) products are terminal — no re-open toggle.
                  if (r.status === "archived") {
                    return (
                      <span className="text-muted-foreground text-sm">—</span>
                    );
                  }
                  return (
                    <Switch
                      checked={r.isActive}
                      onCheckedChange={() => onToggleActive(r)}
                      aria-label={r.isActive ? "ປິດການຂາຍ" : "ເປີດການຂາຍ"}
                    />
                  );
                },
              } as TanstackReactTable.ColumnDef<ShopSellableCatalogRow>,
            ]
          : []),
        ...(canUpdate && (onEdit || onDelete)
          ? [
              {
                id: "actions",
                size: 84,
                cell: ({ row }) => {
                  const r = row.original;
                  const actions = [];
                  // Rejected (archived) products can't be edited (detail is gone),
                  // but can still be deleted (unlinked) from the shop.
                  if (onEdit && r.status !== "archived") {
                    actions.push({
                      label: "ແກ້ໄຂ",
                      icon: <EditIcon className="size-4" />,
                      onClick: () => onEdit(r),
                    });
                  }
                  if (onDelete) {
                    actions.push({
                      label: "ລຶບ",
                      // outline = ບໍ່ມີພື້ນຫຼັງ (ບໍ່ໃຊ້ secondary bg) — ໄອຄອນແດງພໍ
                      variant: "outline" as const,
                      icon: <Trash2Icon className="size-4 text-destructive" />,
                      onClick: () => onDelete(r),
                    });
                  }
                  if (actions.length === 0) return null;
                  return <RowActions actions={actions} maxInline={1} />;
                },
              } as TanstackReactTable.ColumnDef<ShopSellableCatalogRow>,
            ]
          : []),
      ],
      [canUpdate, onEdit, onDelete, onToggleActive, mediaCounts, onViewImages],
    );

  return (
    <DataTable<ShopSellableCatalogRow, unknown>
      noDataMessage="ຍັງບໍ່ມີສິນຄ້າທີ່ມອບໃຫ້ຮ້ານນີ້ ຫຼື ບໍ່ກົງກັບຕົວກອງ."
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
    />
  );
}
